import type { JSONContent } from '@tiptap/core';
import { v4 as uuid } from 'uuid';
import { ACCORDION_NODE_NAME } from '../editor/extensions/accordion';
import { COLUMNS_NODE_NAME } from '../editor/extensions/columns';
import { CUSTOM_COMPONENT_NODE_NAME } from '../editor/extensions/custom-component/custom-component.model';
import { KEY_POINT_CALLOUT_NODE_NAME } from '../editor/extensions/key-point-callout';
import { FORMULA_CALLOUT_NODE_NAME } from '../editor/extensions/formula-callout';
import { SIMPLE_CALLOUT_NODE_NAME } from '../editor/extensions/simple-callout';
import type { MarkdownBlock } from '../model';
import { ContentBlock } from '../model';
import type { ConverterContext } from './blocks-to-tiptap.converter';

export type TiptapToBlockConverter = (json: JSONContent, context?: ConverterContext) => ContentBlock;

let convertersCache: Record<string, TiptapToBlockConverter> | null = null;
let markdownHelperCache: {
  convertTiptapToMarkdownBlock: (
    nodes: JSONContent[],
    id: string,
    customCss?: string,
    context?: ConverterContext,
    customStyle?: Record<string, string>,
  ) => MarkdownBlock;
} | null = null;

function getConverters(): Record<string, TiptapToBlockConverter> {
  if (!convertersCache) {
    // Using require() for lazy loading to avoid circular dependency issues between converters
    /* eslint-disable @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports */
    const { convertTiptapToCalloutBlock } = require('./block-converters/callout-block.converter');
    const { convertTiptapToTableBlock } = require('./block-converters/table-block.converter');
    const { convertTiptapToToggleBlock } = require('./block-converters/toggle-block.converter');
    const { convertTiptapToSuperLatexBlock } = require('./block-converters/superlatex-block.converter');
    const { convertTiptapToSolverBlock } = require('./block-converters/solver-block.converter');
    const { convertTiptapToSimpleSolverBlock } = require('./block-converters/simple-solver-block.converter');
    const { convertTiptapToWidgetBlock } = require('./block-converters/widget-block.converter');
    const { convertTiptapToImageBlock } = require('./block-converters/image-block.converter');
    const { convertTiptapToAccordionBlock } = require('./block-converters/accordion-block.converter');
    const { convertTiptapToSimpleCalloutBlock } = require('./block-converters/simple-callout-block.converter');
    const { convertTiptapToCustomComponentBlock } = require('./block-converters/custom-component-block.converter');
    const { convertTiptapToColumnsBlock } = require('./block-converters/columns-block.converter');
    const { convertTiptapToGojsDiagramBlock } = require('./block-converters/gojs-diagram-block.converter');
    const { convertTiptapToDataDisplayBlock } = require('./block-converters/data-display-block.converter');
    const { convertTiptapToFormulaCalloutBlock } = require('./block-converters/formula-callout-block.converter');
    /* eslint-enable @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports */

    convertersCache = {
      [KEY_POINT_CALLOUT_NODE_NAME]: convertTiptapToCalloutBlock,
      [SIMPLE_CALLOUT_NODE_NAME]: convertTiptapToSimpleCalloutBlock,
      table: convertTiptapToTableBlock,
      details: convertTiptapToToggleBlock,
      superLatex: convertTiptapToSuperLatexBlock,
      solver: convertTiptapToSolverBlock,
      simpleSolver: convertTiptapToSimpleSolverBlock,
      widget: convertTiptapToWidgetBlock,
      image: convertTiptapToImageBlock,
      [ACCORDION_NODE_NAME]: convertTiptapToAccordionBlock,
      [CUSTOM_COMPONENT_NODE_NAME]: convertTiptapToCustomComponentBlock,
      [COLUMNS_NODE_NAME]: convertTiptapToColumnsBlock,
      gojsDiagram: convertTiptapToGojsDiagramBlock,
      dataDisplay: convertTiptapToDataDisplayBlock,
      [FORMULA_CALLOUT_NODE_NAME]: convertTiptapToFormulaCalloutBlock,
    };
  }
  return convertersCache;
}

function getMarkdownHelper(): NonNullable<typeof markdownHelperCache> {
  if (!markdownHelperCache) {
    // Using require() for lazy loading to avoid circular dependency issues between converters
    /* eslint-disable @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports */
    const { convertTiptapToMarkdownBlock } = require('./block-converters/markdown-block.converter');
    /* eslint-enable @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports */

    markdownHelperCache = { convertTiptapToMarkdownBlock };
  }
  return markdownHelperCache;
}

/**
 * Converts a Tiptap document JSON to an array of ContentBlocks.
 */
export function tiptapToContentBlocks(json: JSONContent, context?: ConverterContext): ContentBlock[] {
  if (json.type !== 'doc' || !json.content) {
    return [];
  }
  return convertTiptapNodesToBlocks(json.content, context);
}

/**
 * Converts Tiptap JSON nodes to ContentBlocks.
 * Each node becomes its own ContentBlock.
 * This is the recursive helper used by block converters for nested content.
 */
export function convertTiptapNodesToBlocks(nodes: JSONContent[], context?: ConverterContext): ContentBlock[] {
  if (!nodes.length) {
    return [];
  }

  const converters = getConverters();
  const { convertTiptapToMarkdownBlock } = getMarkdownHelper();

  const result: ContentBlock[] = [];

  for (const node of nodes) {
    const nodeType = node.type ?? '';

    const specialConverter = converters[nodeType];

    if (specialConverter) {
      try {
        result.push(specialConverter(node, context));
      } catch {
        console.error('Failed to convert Tiptap node to block:', node);
      }
    } else {
      // Convert to MarkdownBlock (handles both known markdown nodes and unknown types)
      // Preserve textAlign as custom_style since markdown has no syntax for it
      // (text color is preserved inline via {{color:...}} markers in the markdown)
      const textAlign = node.attrs?.['textAlign'];
      const customStyle = {
        ...(textAlign && textAlign !== 'left' ? { 'text-align': textAlign } : {}),
      };
      result.push(
        convertTiptapToMarkdownBlock(
          [node],
          (node.attrs?.['id'] as string) ?? uuid(),
          undefined,
          context,
          Object.keys(customStyle).length > 0 ? customStyle : undefined,
        ),
      );
    }
  }

  return result;
}
